import {
  ADF,
  Asset,
  ModuleDefinition,
  WebModuleDefinition,
  ModuleDefinitionVersion,
  WebSurfaceDefinition,
  SurfaceDefinitionVersion,
  SurfaceDefinition,
  ZoneDefinition,
  ModuleRestriction,
  Definition
} from '../';

import {
  FieldDefinition,
  PrimitiveFieldDefinition,
  AssetFieldDefinition,
  BooleanFieldDefinition,
  StringFieldDefinition,
  NumberFieldDefinition,
  RichTextFieldDefinition,
  ObjectFieldDefinition,
  DateFieldDefinition,
  EnumFieldDefinition,
  ArrayFieldDefinition
} from '../schema';

/**
 * Process the definition and extract the fields from definition configs of the appropriate primitive (module, zone, surface).
 * @typedef ProcessDefinition
 * @property - Content primitive definition (one of module, zone, surface)
 *
 */
export type ProcessDefinition<B extends Definition> = {
  readonly name: B['name'];
  readonly schema: B['schema'];
  readonly type: B['type'];
  readonly versions: ProcessVersions<B>;
};

/**
 * Process the version definition and extract the fields from definition configs of the appropriate primitive (module, zone, surface).
 * @typedef ProcessVersions
 * @property - Content primitive version definition (one of module, zone, surface)
 *
 */
export type ProcessVersions<B extends Definition> =
  B extends WebSurfaceDefinition
    ? MapSurfaceDefinitionVersions<B['versions']>
    : B extends WebModuleDefinition
    ? MapModuleDefinitionVersions<B['versions']>
    : never;

/**
 * Process module definition versions for each version of module definition config.
 * @typedef MapModuleDefinitionVersions
 * @property - module definition version
 *
 */
export type MapModuleDefinitionVersions<B extends ModuleDefinitionVersion> = {
  readonly [Version in keyof B]: ProcessModuleDefinition<
    B[Version] extends B[number] ? B[Version] : never
  >;
};

/**
 * Process surface definition versions for each version of surface definition config.
 * @typedef MapSurfaceDefinitionVersions
 * @property - surface definition version
 *
 */
export type MapSurfaceDefinitionVersions<B extends SurfaceDefinitionVersion> = {
  readonly [Version in keyof B]: ProcessSurfaceDefinition<
    B[Version] extends B[number] ? B[Version] : never
  >;
};

/**
 * Process the module definition and extract the fields from module definition configs.
 * @typedef ProcessModuleDefinition
 * @property - Module definition
 *
 */
export type ProcessModuleDefinition<B extends ModuleDefinition> = {
  readonly label: B['label'];
  readonly description: B['description'];
  readonly channels: B['channels'];
  readonly status: B['status'];
  readonly fields: MapFieldDefinitionToContent<B>;
};

/**
 * Process the surface definition and extract the fields from surface definition configs.
 * @typedef ProcessSurfaceDefinition
 * @property - Surface definition
 *
 */
export type ProcessSurfaceDefinition<B extends SurfaceDefinition> = {
  readonly label: B['label'];
  readonly description: B['description'];
  readonly channels: B['channels'];
  readonly status: B['status'];
  readonly fields: MapFieldDefinitionToContent<B>;
} & ZonesProperty<B>;

/**
 * Process the surface and add a zones property to the surface type if the passed in definition contains a zones
 * @typedef ZonesProperty
 * @property - Surface definition
 *
 */
export type ZonesProperty<B extends SurfaceDefinition> =
  'zonesDefinitions' extends keyof B
    ? B['zonesDefinitions'] extends ReadonlyArray<ZoneDefinition>
      ? { readonly zones: ProcessZonesFromSurface<B['zonesDefinitions']> }
      : Record<string, never>
    : Record<string, never>;

/**
 * Process the zone definitions array and create a mapping of the zone name to the properties required by the zone.
 * @typedef ProcessZonesFromSurface
 * @property - Zone definition
 *
 */
export type ProcessZonesFromSurface<B extends ReadonlyArray<ZoneDefinition>> = {
  [key in B[number]['name']]: GetModuleRestrictions<
    Extract<B[number], { name: key }>
  >;
};

/**
 * Process the zone definition and extract the types required by the zone from zone validations configs.
 * @typedef GetModuleRestrictions
 * @property - Zone definition
 *
 */
export type GetModuleRestrictions<B extends ZoneDefinition> = B extends {
  validations: { moduleRestrictions: ReadonlyArray<ModuleRestriction> };
}
  ? B['validations']['moduleRestrictions']
  : ReadonlyArray<ModuleRestriction>;

/**
 * Generate object containing mapping of field name and type from module definition configs array.
 * @typedef MapFieldDefinitionToContent
 * @property - Module definition
 *
 */
export type MapFieldDefinitionToContent<
  B extends ModuleDefinition | SurfaceDefinition
> = ProcessModuleConfig<B['fieldDefinitions']>;

/**
 * Process module definition configs for each type of module definition config.
 * @typedef ProcessModuleConfig
 * @property - Web module definition
 *
 */
export type ProcessModuleConfig<B extends ReadonlyArray<FieldDefinition>> =
  MapModuleConfigToPrimitiveProps<PickPrimitive<B[number]>> &
    MapModuleConfigToAssetProps<PickAsset<B[number]>> &
    MapModuleConfigToDateProps<PickDate<B[number]>> &
    MapModuleConfigToObjectProps<PickObject<B[number]>> &
    MapModuleConfigToEnumProps<PickEnum<B[number]>> &
    MapModuleConfigToRichTextProps<PickRichText<B[number]>> &
    MapModuleConfigToArrayProps<PickArray<B[number]>>;

/**
 * Process the module definition configs for object type config.
 * @typedef MapModuleConfigToObjectProps
 * @property - config of type ModuleDefinitionObjectConfig
 *
 */
export type MapModuleConfigToObjectProps<B extends ObjectFieldDefinition> = {
  readonly [obj in RequiredModuleConfig<B> as obj['name']]: ProcessModuleConfig<
    PickFieldDefinitionsFromObject<obj>
  >;
} & {
  readonly [obj in OptionalModuleConfig<B> as obj['name']]?: ProcessModuleConfig<
    PickFieldDefinitionsFromObject<obj>
  >;
};

/**
 * Process the module definition configs for enum type config.
 * @typedef MapModuleConfigToEnumProps
 * @property - config of type ModuleDefinitionEnumConfig
 *
 */
export type MapModuleConfigToEnumProps<B extends EnumFieldDefinition> = {
  readonly [key in RequiredModuleConfig<B> as key['name']]: MapEnumConfigToModuleType<key>;
} & {
  readonly [key in OptionalModuleConfig<B> as key['name']]?: MapEnumConfigToModuleType<key>;
};

/**
 * Process the module definition configs for array type config.
 * @typedef MapModuleConfigToArrayProps
 * @property - config of type ModuleDefinitionArrayConfig
 *
 */
export type MapModuleConfigToArrayProps<B extends ArrayFieldDefinition> = {
  readonly [key in RequiredModuleConfig<B> as key['name']]: MapModuleDefinitionConfigToArrayModuleType<
    MapArrayConfigToModuleType<key>
  >;
} & {
  readonly [key in OptionalModuleConfig<B> as key['name']]?: MapModuleDefinitionConfigToArrayModuleType<
    MapArrayConfigToModuleType<key>
  >;
};

/**
 * Map the config name with its primitive value, generate a list of field and value as [name as key] : primitive type
 * eg : \{title : string, isValid : boolean, count : number\}
 * @typedef MapModuleConfigToPrimitiveProps
 * @property - config of type ModuleDefinitionPrimitiveTypeConfig
 *
 */
export type MapModuleConfigToPrimitiveProps<
  B extends PrimitiveFieldDefinition
> = {
  readonly [key in RequiredModuleConfig<B> as key['name']]: MapPrimitiveConfigToModuleType<key>;
} & {
  readonly [key in OptionalModuleConfig<B> as key['name']]?: MapPrimitiveConfigToModuleType<key>;
};

/**
 * Map the config name with its asset value, generate a list of field and value as [name as key] : string type
 * eg : \{"url": "//folder_path/Insight_in_JSM_landing_page.png"\}
 * @typedef MapModuleConfigToAssetProps
 * @property - config of type ModuleDefinitionAssetConfig
 *
 */
export type MapModuleConfigToAssetProps<B extends AssetFieldDefinition> = {
  readonly [key in RequiredModuleConfig<B> as key['name']]: Asset;
} & {
  readonly [key in OptionalModuleConfig<B> as key['name']]?: Asset;
};

/**
 * Map the config name with its date value, generate a list of field and value as [name as key] : string type
 * eg : \{birthday: string\}
 * @typedef MapModuleConfigToDateProps
 * @property - config of type ModuleDefinitionDateConfig
 *
 */
export type MapModuleConfigToDateProps<B extends DateFieldDefinition> = {
  readonly [key in RequiredModuleConfig<B> as key['name']]: string;
} & {
  readonly [key in OptionalModuleConfig<B> as key['name']]?: string;
};

/**
 * Map the config name with its richtext value, generate a list of field and value as [name as key] : ADF type
 * eg : \{document : ADF\}
 * @typedef MapModuleConfigToRichTextProps
 * @property - config of type ModuleDefinitionRichTextConfig
 *
 */
export type MapModuleConfigToRichTextProps<B extends RichTextFieldDefinition> =
  {
    readonly [key in RequiredModuleConfig<B> as key['name']]: ADF;
  } & {
    readonly [key in OptionalModuleConfig<B> as key['name']]?: ADF;
  };

/**
 * Generate a list on only primitive type config
 * @typedef PickPrimitive
 * @property - config of type ModuleDefinition
 *
 */
export type PickPrimitive<B extends FieldDefinition> =
  B extends PrimitiveFieldDefinition ? B : never;

/**
 * Generate a list on only asset type config
 * @typedef PickAsset
 * @property - config of type ModuleDefinition
 *
 */
export type PickAsset<B extends FieldDefinition> =
  B extends AssetFieldDefinition ? B : never;

/**
 * Generate a list on only date type config
 * @typedef PickDate
 * @property - config of type ModuleDefinition
 *
 */
export type PickDate<B extends FieldDefinition> = B extends DateFieldDefinition
  ? B
  : never;

/**
 * Generate a list on only object type config
 * @typedef PickObject
 * @property - config of type ModuleDefinition
 *
 */
export type PickObject<B extends FieldDefinition> =
  B extends ObjectFieldDefinition ? B : never;

/**
 * Generate a list on only enum type config
 * @typedef PickEnum
 * @property - config of type ModuleDefinition
 *
 */
export type PickEnum<B extends FieldDefinition> = B extends EnumFieldDefinition
  ? B
  : never;

/**
 * Generate a list on only richtext type config
 * @typedef PickRichText
 * @property - config of type ModuleDefinition
 *
 */
export type PickRichText<B extends FieldDefinition> =
  B extends RichTextFieldDefinition ? B : never;

/**
 * Get a list of field definitions from a object type config.
 * @typedef PickFieldDefinitionsFromObject
 * @property - config of type ModuleDefinition
 *
 */
export type PickFieldDefinitionsFromObject<B extends ObjectFieldDefinition> =
  B extends ObjectFieldDefinition ? B['fieldDefinitions'] : never;

/**
 * Generate a list on only array type config
 * @typedef PickArray
 * @property - config of type ModuleDefinition
 *
 */
export type PickArray<B extends FieldDefinition> =
  B extends ArrayFieldDefinition ? B : never;

/**
 * Get a list of configs from a enum type config.
 * @typedef MapEnumConfigToModuleType
 * @property - config of type ModuleDefinitionEnumConfig
 *
 */
export type MapEnumConfigToModuleType<B extends EnumFieldDefinition> =
  B extends EnumFieldDefinition ? B['validations']['cases'][number] : never;

/**
 * Get a list of configs from a object type config.
 * @typedef MapArrayConfigToModuleType
 * @property - config of type ModuleDefinitionArrayConfig
 *
 */
export type MapArrayConfigToModuleType<B extends ArrayFieldDefinition> =
  B extends ArrayFieldDefinition ? B['fieldDefinition'] : never;

/**
 * Mapping of field name with field type as
 */
export type MapModuleDefinitionConfigToArrayModuleType<
  B extends FieldDefinition
> = B extends BooleanFieldDefinition
  ? boolean[]
  : B extends NumberFieldDefinition
  ? number[]
  : B extends StringFieldDefinition
  ? string[]
  : B extends RichTextFieldDefinition
  ? ADF[]
  : B extends ObjectFieldDefinition
  ? ReadonlyArray<ProcessModuleConfig<PickFieldDefinitionsFromObject<B>>>
  : B extends ArrayFieldDefinition
  ? ReadonlyArray<MapModuleConfigToArrayProps<B>>
  : unknown;

/**
 * Mapping of field name with field type as [name as key] : primitive type.
 * eg : \{title : string, isValid : boolean, count : number\}
 * @typedef MapPrimitiveConfigToModuleType
 * @property - config of type ModuleDefinitionPrimitiveTypeConfig
 *
 */
export type MapPrimitiveConfigToModuleType<B extends PrimitiveFieldDefinition> =
  B extends BooleanFieldDefinition
    ? boolean
    : B extends NumberFieldDefinition
    ? number
    : B extends StringFieldDefinition
    ? string
    : unknown;

/**
 * Get list of configs with required fields
 * @typedef RequiredModuleConfig
 * @property - config of type ModuleDefinition
 *
 */
export type RequiredModuleConfig<B extends FieldDefinition> =
  B extends FieldDefinition
    ? IsRequiredValidationFromObject<B> extends true
      ? B
      : never
    : never;

/**
 * Get list of configs with optional fields
 * @typedef OptionalModuleConfig
 * @property - config of type ModuleDefinition
 *
 */
export type OptionalModuleConfig<B extends FieldDefinition> =
  B extends FieldDefinition
    ? IsRequiredValidationFromObject<B> extends false
      ? B
      : never
    : never;

/**
 * Checks the validation object to see if the field is required or optional
 * @typedef IsRequiredValidationFromObject
 * @property - config of type ModuleDefinition
 *
 */
export type IsRequiredValidationFromObject<B extends FieldDefinition> =
  B['validations'] extends { isRequired?: boolean }
    ? B['validations']['isRequired'] extends true
      ? true
      : false
    : false;
